import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/_components/select";
import { ContactInfo, RecipientsResponse, TransMasterData, RecipientItem } from "../../types";
import { useContactSelection } from "./hooks/useContactSelection";

interface EmailSelectorProps {
  contactInfo: ContactInfo;
  setContactInfo: React.Dispatch<React.SetStateAction<ContactInfo>>;
  recipients: RecipientsResponse;
  transMasterData: TransMasterData | null;
  setSelectedCandidate: React.Dispatch<React.SetStateAction<RecipientItem | null>>;
}

export const EmailSelector: React.FC<EmailSelectorProps> = ({
  contactInfo,
  setContactInfo,
  recipients,
  setSelectedCandidate
}) => {
  const { handleContactSelection, removeSelection } = useContactSelection({
    contactInfo,
    setContactInfo,
    setSelectedCandidate
  });

  if (recipients.emailIds.length === 0) {
    return null;
  }

  const isDisabled = !!contactInfo.phone;

  const handleValueChange = (value: string) => {
    if (value === "remove") {
      removeSelection("email");
    } else {
      const recipient = recipients.emailIds.find(item => item.emailId === value);
      handleContactSelection("email", value, recipient);
    }
  };

  const getLabelText = () => {
    let text = "Email Address";
    if (contactInfo.email) text += " (Selected)";
    if (isDisabled) text += " (Disabled - Phone selected)";
    return text;
  };

  const getSelectClassName = () => {
    if (isDisabled) {
      return "border-muted bg-muted text-muted-foreground cursor-not-allowed";
    }
    if (contactInfo.email) {
      return "border-green-500 bg-green-50 dark:bg-green-950 dark:border-green-400";
    }
    return "";
  };

  return (
    <div>
      <label 
        htmlFor="email-select" 
        className={`text-sm font-medium ${isDisabled ? 'text-muted-foreground' : 'text-foreground'}`}
      >
        {getLabelText()}
      </label>
      
      <Select
        key={`email-${contactInfo.email}-${contactInfo.phone}`}
        value={contactInfo.email || undefined}
        onValueChange={handleValueChange}
        disabled={isDisabled}
      >
        <SelectTrigger className={`mt-1 ${getSelectClassName()}`}>
          <SelectValue
            placeholder={isDisabled ? "Disabled" : "Select an email address"}
          />
        </SelectTrigger>
        
        <SelectContent>
          {contactInfo.email && (
            <>
              <SelectItem value="remove" className="text-red-600 dark:text-red-400 font-medium">
                ❌ Remove Email Selection
              </SelectItem>
              <div className="border-t border-border my-1"></div>
            </>
          )}
          {recipients.emailIds
            .filter((item): item is typeof item & { emailId: string } => 
              !!item.emailId && item.emailId.trim() !== ""
            )
            .map((item, index) => (
              <SelectItem 
                key={index} 
                value={item.emailId}
              >
                {item.emailId} ({item.fullName} - {item.recipientId})
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};